import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'

export default function VendorDetail({ vendor, onClose }) {
  const { user } = useAuth()
  const [comments, setComments] = useState([])
  const [avgRating, setAvgRating] = useState(null)
  const [ratingCount, setRatingCount] = useState(0)
  const [userRating, setUserRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [newComment, setNewComment] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  const vendorInfo = vendor.vendors || {}
  const vendorPermit = vendor.vendor_permit

  // Group schedules by day
  const schedulesByDay = {}
  const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

  for (const schedule of vendor.schedules || []) {
    const day = schedule.day_of_week
    if (!schedulesByDay[day]) {
      schedulesByDay[day] = []
    }
    schedulesByDay[day].push(schedule)
  }

  const sortedDays = dayOrder.filter(day => schedulesByDay[day])

  useEffect(() => {
    fetchReviews()
  }, [vendorPermit])

  const fetchReviews = async () => {
    try {
      const response = await fetch(`/.netlify/functions/get-vendor-reviews?vendor_permit=${encodeURIComponent(vendorPermit)}`)
      const result = await response.json()

      if (response.ok) {
        setComments(result.comments || [])
        setAvgRating(result.avgRating)
        setRatingCount(result.ratingCount)
      }
    } catch (error) {
      console.error('Error fetching reviews:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleRating = async (rating) => {
    if (!user) return

    setUserRating(rating)
    try {
      const response = await fetch('/.netlify/functions/set-rating', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vendor_permit: vendorPermit,
          user_id: user.id,
          rating
        })
      })

      const result = await response.json()
      if (response.ok) {
        setAvgRating(result.avgRating)
        setRatingCount(result.ratingCount)
      }
    } catch (error) {
      console.error('Error setting rating:', error)
    }
  }

  const handleSubmitComment = async (e) => {
    e.preventDefault()
    if (!user || !newComment.trim()) return

    setSubmitting(true)
    try {
      const response = await fetch('/.netlify/functions/add-comment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vendor_permit: vendorPermit,
          user_id: user.id,
          content: newComment
        })
      })

      const result = await response.json()
      if (response.ok) {
        setComments([result.data, ...comments])
        setNewComment('')
      }
    } catch (error) {
      console.error('Error adding comment:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getEmailPrefix = (email) => {
    if (!email) return 'Anonymous'
    return email.split('@')[0]
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        <div className="vendor-detail">
          <div className="vendor-detail-header">
            <h2>{vendor.applicant}</h2>
            {vendorInfo.food_items && (
              <p className="vendor-food">{vendorInfo.food_items}</p>
            )}
            {vendor.facility_type && (
              <span className="tag" style={{ marginTop: '8px', display: 'inline-block' }}>{vendor.facility_type}</span>
            )}
          </div>

          <div className="vendor-schedule-section">
            <h3>Schedule</h3>
            <div className="schedule-grid">
              {sortedDays.map(day => (
                <div key={day} className="schedule-grid-row">
                  <span className="schedule-grid-day">{day}</span>
                  <div className="schedule-grid-times">
                    {schedulesByDay[day].map((s, i) => (
                      <div key={i} className="schedule-slot">
                        <span className="schedule-time">{s.start_24} - {s.end_24}</span>
                        {s.location_description && (
                          <span className="schedule-location">{s.location_description}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="vendor-rating-section">
            <h3>Rating</h3>
            <div className="rating-display">
              {avgRating ? (
                <>
                  <span className="rating-value">{avgRating.toFixed(1)}</span>
                  <div className="stars">
                    {[1, 2, 3, 4, 5].map(star => (
                      <span key={star} className={star <= Math.round(avgRating) ? 'star filled' : 'star'}>
                        ★
                      </span>
                    ))}
                  </div>
                  <span className="rating-count">({ratingCount} ratings)</span>
                </>
              ) : (
                <span className="no-ratings">No ratings yet</span>
              )}
            </div>

            {user ? (
              <div className="user-rating">
                <span>Your rating:</span>
                <div className="stars interactive">
                  {[1, 2, 3, 4, 5].map(star => (
                    <span
                      key={star}
                      className={star <= (hoverRating || userRating) ? 'star filled' : 'star'}
                      onClick={() => handleRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                    >
                      ★
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <p className="sign-in-prompt">Sign in to rate this vendor</p>
            )}
          </div>

          <div className="vendor-comments-section">
            <h3>Comments ({comments.length})</h3>

            {user ? (
              <form onSubmit={handleSubmitComment} className="comment-form">
                <textarea
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  placeholder="Share your experience with this vendor..."
                  rows={3}
                />
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting || !newComment.trim()}
                >
                  {submitting ? 'Posting...' : 'Post Comment'}
                </button>
              </form>
            ) : (
              <p className="sign-in-prompt">Sign in to leave a comment</p>
            )}

            {loading ? (
              <div className="loading-comments">Loading comments...</div>
            ) : comments.length === 0 ? (
              <div className="no-comments">No comments yet. Be the first to share your experience!</div>
            ) : (
              <div className="comments-list">
                {comments.map(comment => (
                  <div key={comment.id} className="comment">
                    <div className="comment-header">
                      <span className="comment-author">{getEmailPrefix(comment.profiles?.email)}</span>
                      <span className="comment-date">{formatDate(comment.created_at)}</span>
                    </div>
                    <p className="comment-content">{comment.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
